import { useState, useEffect } from "react"
import { CheckCircle, Clock, Search, ExternalLink, Eye } from "lucide-react"
import { useNavigate } from "react-router-dom"
import { Button, Input, Card, CardBody, Modal, ModalContent, ModalHeader, ModalBody, ModalFooter, Spinner, Select, SelectItem, Skeleton } from "@heroui/react"
import { useInView } from "react-intersection-observer"
import { usePendingReviewPascosInfinite, useReviewPasco, useScheduleReviewPasco } from "@src/presentation/hooks/pasco"
import { useUniversities } from "@src/presentation/hooks/universities"
import { useViewedPascos } from "@src/presentation/contexts/ViewedPascosContext"
import { ToastService } from "@src/shared/services/toast.service"
import { formatDateHuman } from "@src/shared/utils/date-formatter"
import type { PascoListItemDTO } from "@src/application/pascos/dto"
import type { InfiniteData } from '@tanstack/react-query'

export default function PendingReviewPage() {
    const navigate = useNavigate()
    const [searchQuery, setSearchQuery] = useState("")
    const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("")
    const [universityFilter, setUniversityFilter] = useState<string>("")
    const [semesterFilter, setSemesterFilter] = useState<string>("")
    const [academicYearFilter, setAcademicYearFilter] = useState<string>("")
    const [confirmModalOpen, setConfirmModalOpen] = useState(false)
    const [actionType, setActionType] = useState<"review" | "schedule-review" | null>(null)
    const [targetPascoId, setTargetPascoId] = useState<string | null>(null)
    const { markPascoAsViewed, isPascoViewed } = useViewedPascos()

    // Debounce search query
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearchQuery(searchQuery)
        }, 300)

        return () => clearTimeout(timer)
    }, [searchQuery])

    // Fetch universities for filter
    const { data: universitiesData } = useUniversities()
    const universities = universitiesData?.universities || []

    // Fetch pending review PASCOs with infinite scroll
    const {
        data,
        isLoading,
        isFetching,
        fetchNextPage,
        hasNextPage,
        isFetchingNextPage,
        refetch
    } = usePendingReviewPascosInfinite(
        {
            q: debouncedSearchQuery || undefined,
            university_id: universityFilter || undefined,
            semester: semesterFilter || undefined,
            academic_year: academicYearFilter || undefined,
        },
        20
    )

    // Flatten all pages into a single array
    const allPascos = (data as unknown as InfiniteData<{ pascos: PascoListItemDTO[]; total: number; hasMore: boolean }>)?.pages?.flatMap((page) => page.pascos) || []

    // Get total count from the last page
    const totalCount = (data as unknown as InfiniteData<{ pascos: PascoListItemDTO[]; total: number; hasMore: boolean }>)?.pages?.[(data as unknown as InfiniteData<{ pascos: PascoListItemDTO[]; total: number; hasMore: boolean }>).pages.length - 1]?.total || 0

    // Extract unique semesters and academic years from fetched data for filters
    const availableSemesters = Array.from(new Set(allPascos.map(p => p.semester?.toString()).filter(Boolean))).sort()
    const availableAcademicYears = Array.from(new Set(allPascos.map(p => p.academicYear).filter(Boolean))).sort().reverse()

    // Only show skeleton on initial load, not on refetches
    const isInitialLoading = isLoading && !data
    const isRefetching = isFetching && !isLoading && !isFetchingNextPage

    // Review/Schedule Review mutations
    const { mutate: reviewPasco, isPending: isReviewing } = useReviewPasco({
        onSuccess: () => {
            ToastService.success(actionType === "review" ? "PASCO marked as reviewed" : "PASCO scheduled for review")
            setConfirmModalOpen(false)
            setTargetPascoId(null)
            setActionType(null)
            refetch()
        },
        onError: (error: any) => {
            ToastService.error(error?.message || `Failed to ${actionType === "review" ? "review" : "schedule review for"} PASCO`)
        }
    })

    const { mutate: scheduleReviewPasco, isPending: isScheduling } = useScheduleReviewPasco({
        onSuccess: () => {
            ToastService.success("PASCO scheduled for review")
            setConfirmModalOpen(false)
            setTargetPascoId(null)
            setActionType(null)
            refetch()
        },
        onError: (error: any) => {
            ToastService.error(error?.message || "Failed to schedule PASCO for review")
        }
    })

    // Infinite scroll logic using Intersection Observer
    const { ref, inView } = useInView()

    // Load more when trigger element comes into view
    useEffect(() => {
        if (inView && hasNextPage && !isFetchingNextPage) {
            fetchNextPage()
        }
    }, [inView, hasNextPage, isFetchingNextPage, fetchNextPage])

    const handleReview = (id: string) => {
        setTargetPascoId(id)
        setActionType("review")
        setConfirmModalOpen(true)
    }

    const handleViewDetails = (id: string) => {
        markPascoAsViewed(id)
        navigate(`/pascos/${id}`)
    }

    const confirmAction = () => {
        if (!targetPascoId || !actionType) return

        if (actionType === "review") {
            reviewPasco(targetPascoId)
        } else if (actionType === "schedule-review") {
            scheduleReviewPasco(targetPascoId)
        }
    }

    return (
        <div className="min-h-screen bg-background">
            {/* Header */}
            <header className="border-b border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 sticky top-0 z-20">
                <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex flex-col sm:flex-row h-auto sm:h-16 py-3 sm:py-0 items-start sm:items-center justify-between gap-3 sm:gap-0">
                        <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
                            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-500">
                                <Eye className="h-4 sm:h-5 w-4 sm:w-5 text-blue-500-foreground" />
                            </div>
                            <h1 className="text-lg sm:text-xl font-semibold text-foreground">Pending Review</h1>
                            <span className="rounded-full bg-blue-500/10 px-2.5 py-0.5 text-xs font-medium text-blue-500">
                                {totalCount} pending
                            </span>
                        </div>
                    </div>
                </div>
            </header>

            {/* Main Content */}
            <main className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
                <div className="space-y-6">
                    {/* Info Banner */}
                    <Card className="border border-blue-500/20 bg-card rounded shadow-none hover:shadow-custom transition-shadow">
                        <CardBody className="p-4">
                            <div className="flex gap-3">
                                <Eye className="h-5 w-5 text-blue-500 flex-shrink-0 mt-0.5" />
                                <div className="flex-1">
                                    <h3 className="text-sm font-medium text-blue-500">Review Required</h3>
                                    <p className="mt-1 text-sm text-default-600">
                                        These PASCOs have been approved and are ready for review. During review, check that the PASCO information matches the metadata and content. Reviewed PASCOs are considered complete and ready for use.
                                    </p>
                                </div>
                            </div>
                        </CardBody>
                    </Card>

                    {/* Search and Filters */}
                    <Card className="border border-border bg-card rounded shadow-none hover:shadow-custom transition-shadow">
                        <CardBody className="p-4 space-y-4">
                            <Input
                                type="text"
                                placeholder="Search by course code, name, or year..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                startContent={<Search className="h-4 w-4 text-default-400" />}
                                classNames={{
                                    input: "text-sm",
                                    inputWrapper: "h-10 rounded",
                                }}
                            />

                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                                <Select
                                    label="University"
                                    placeholder="Select university"
                                    selectedKeys={universityFilter ? [universityFilter] : ["all"]}
                                    onSelectionChange={(keys) => {
                                        const selected = Array.from(keys)[0] as string
                                        setUniversityFilter(selected === "all" ? "" : selected)
                                    }}
                                    classNames={{
                                        trigger: "h-10 rounded",
                                    }}
                                >
                                    <SelectItem key="all" className="rounded">All</SelectItem>
                                    {universities.map((university) => (
                                        <SelectItem key={university.university_id} className="rounded">
                                            {university.name}
                                        </SelectItem>
                                    )) as any}
                                </Select>

                                <Select
                                    label="Semester"
                                    placeholder="Select semester"
                                    selectedKeys={semesterFilter ? [semesterFilter] : ["all"]}
                                    onSelectionChange={(keys) => {
                                        const selected = Array.from(keys)[0] as string
                                        setSemesterFilter(selected === "all" ? "" : selected)
                                    }}
                                    classNames={{
                                        trigger: "h-10 rounded",
                                    }}
                                >
                                    <SelectItem key="all" className="rounded">All</SelectItem>
                                    {availableSemesters.map((semester) => (
                                        <SelectItem key={semester} className="rounded">
                                            Semester {semester}
                                        </SelectItem>
                                    )) as any}
                                </Select>

                                <Select
                                    label="Academic Year"
                                    placeholder="Select year"
                                    selectedKeys={academicYearFilter ? [academicYearFilter] : ["all"]}
                                    onSelectionChange={(keys) => {
                                        const selected = Array.from(keys)[0] as string
                                        setAcademicYearFilter(selected === "all" ? "" : selected)
                                    }}
                                    classNames={{
                                        trigger: "h-10 rounded",
                                    }}
                                >
                                    <SelectItem key="all" className="rounded">All</SelectItem>
                                    {availableAcademicYears.map((year) => (
                                        <SelectItem key={year} className="rounded">
                                            {year}
                                        </SelectItem>
                                    )) as any}
                                </Select>
                            </div>
                        </CardBody>
                    </Card>

                    {/* Pending Review PASCOs List */}
                    <div className="border border-border bg-card rounded shadow-none hover:shadow-custom transition-shadow">
                        {/* Initial Loading Shimmer */}
                        {isInitialLoading ? (
                            <div className="divide-y divide-border">
                                {[1, 2, 3, 4, 5].map((i) => (
                                    <div key={i} className="p-6">
                                        <div className="flex items-start gap-4">
                                            <div className="flex-1 space-y-3">
                                                <div className="flex items-center gap-2 flex-wrap">
                                                    <Skeleton className="h-6 w-20 rounded" />
                                                    <Skeleton className="h-5 w-24 rounded-full" />
                                                    <Skeleton className="h-5 w-20 rounded-full" />
                                                </div>
                                                <Skeleton className="h-5 w-3/4 rounded" />
                                                <Skeleton className="h-4 w-full rounded" />
                                            </div>
                                            <div className="flex gap-2">
                                                <Skeleton className="h-9 w-28 rounded" />
                                                <Skeleton className="h-9 w-32 rounded" />
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <>
                                {/* Refetching indicator */}
                                {isRefetching && (
                                    <div className="p-3 bg-primary/5 border-b border-primary/20">
                                        <div className="flex items-center justify-center gap-2 text-primary">
                                            <Spinner size="sm" color="primary" />
                                            <span className="text-xs">Updating results...</span>
                                        </div>
                                    </div>
                                )}

                                {/* List */}
                                <div className="divide-y divide-border">
                                    {allPascos.map((pasco) => (
                                        <div
                                            key={pasco.pascoId}
                                            className="p-6 transition-colors hover:bg-muted/50"
                                        >
                                            <div className="flex items-start gap-4">
                                                {/* Content */}
                                                <div className="flex-1 min-w-0">
                                                    <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-3 lg:gap-4">
                                                        <div className="flex-1 min-w-0">
                                                            <div className="flex items-center gap-2 sm:gap-3 mb-2 flex-wrap">
                                                                <span className="rounded bg-primary/10 px-2 py-1 text-xs sm:text-sm font-semibold text-primary">
                                                                    {pasco.courseCode}
                                                                </span>
                                                                <span
                                                                    className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium ${pasco.processingStatus === "completed"
                                                                        ? "bg-success/10 text-success border-success/20"
                                                                        : pasco.processingStatus === "processing"
                                                                            ? "bg-blue-500/10 text-blue-500 border-blue-500/20"
                                                                            : pasco.processingStatus === "failed"
                                                                                ? "bg-destructive/10 text-destructive border-destructive/20"
                                                                                : "bg-warning/10 text-warning border-warning/20"
                                                                        }`}
                                                                >
                                                                    {pasco.processingStatus}
                                                                </span>
                                                                <span className="inline-flex items-center rounded-full border border-success/20 bg-success/10 px-2.5 py-0.5 text-xs font-medium text-success">
                                                                    Approved
                                                                </span>
                                                            </div>
                                                            <h3 className="text-sm sm:text-base font-medium text-foreground mb-1">
                                                                {pasco.courseName}
                                                            </h3>
                                                            <div className="flex flex-wrap gap-x-2 sm:gap-x-4 gap-y-1 text-xs sm:text-sm text-muted-foreground">
                                                                <span>{pasco.academicYear}</span>
                                                                <span className="hidden sm:inline">•</span>
                                                                <span>Semester {pasco.semester}</span>
                                                                <span className="hidden sm:inline">•</span>
                                                                <span>Level {pasco.level}</span>
                                                                <span className="hidden sm:inline">•</span>
                                                                <span className="hidden sm:inline">Uploaded {formatDateHuman(pasco.createdAt)}</span>
                                                            </div>
                                                        </div>

                                                        {/* Actions */}
                                                        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 w-full lg:w-auto">
                                                            <Button
                                                                size="sm"
                                                                variant="flat"
                                                                startContent={<ExternalLink className="h-3 sm:h-4 w-3 sm:w-4" />}
                                                                onPress={() => handleViewDetails(pasco.pascoId)}
                                                                className="rounded justify-center"
                                                            >
                                                                <span className="text-xs sm:text-sm">View Details</span>
                                                            </Button>
                                                            {!pasco.isReviewed && (
                                                                <Button
                                                                    size="sm"
                                                                    color="success"
                                                                    startContent={<CheckCircle className="h-3 sm:h-4 w-3 sm:w-4" />}
                                                                    isDisabled={!isPascoViewed(pasco.pascoId)}
                                                                    onPress={() => handleReview(pasco.pascoId)}
                                                                    className="rounded justify-center"
                                                                >
                                                                    <span className="text-xs sm:text-sm">Mark Reviewed</span>
                                                                </Button>

                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    ))}

                                    {/* Infinite scroll loading indicator */}
                                    {isFetchingNextPage && (
                                        <div className="flex items-center justify-center py-8">
                                            <div className="flex items-center gap-2 text-muted-foreground">
                                                <Spinner size="sm" color="primary" />
                                                <span className="text-sm">Loading more PASCOs...</span>
                                            </div>
                                        </div>
                                    )}

                                    {/* Infinite scroll trigger element */}
                                    {hasNextPage && !isFetchingNextPage && (
                                        <div ref={ref} className="h-4" />
                                    )}

                                    {allPascos.length === 0 && !isFetchingNextPage && (
                                        <div className="flex flex-col items-center justify-center py-12 text-center">
                                            <CheckCircle className="h-12 w-12 text-success" />
                                            <h3 className="mt-4 text-sm font-medium text-foreground">All caught up!</h3>
                                            <p className="mt-1 text-sm text-muted-foreground">
                                                {searchQuery || universityFilter || semesterFilter || academicYearFilter
                                                    ? "No pending reviews match your filters."
                                                    : "There are no PASCOs pending review."}
                                            </p>
                                            {!searchQuery && !universityFilter && !semesterFilter && !academicYearFilter && (
                                                <Button
                                                    variant="bordered"
                                                    onPress={() => navigate("/pascos")}
                                                    className="mt-4 rounded"
                                                >
                                                    View All PASCOs
                                                </Button>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </main>

            {/* Confirmation Modal */}
            <Modal
                isOpen={confirmModalOpen}
                onClose={() => setConfirmModalOpen(false)}
                classNames={{
                    base: "rounded border border-border",
                }}
                backdrop="blur"
            >
                <ModalContent>
                    <ModalHeader className="flex flex-col gap-1">
                        <div className="flex items-center gap-2">
                            {actionType === "review" ? (
                                <CheckCircle className="h-5 w-5 text-success" />
                            ) : (
                                <Clock className="h-5 w-5 text-warning" />
                            )}
                            <span>
                                {actionType === "review" ? "Mark PASCO as Reviewed" : "Schedule PASCO for Review"}
                            </span>
                        </div>
                    </ModalHeader>
                    <ModalBody>
                        <p className="text-foreground-600">
                            {actionType === "review"
                                ? "Are you sure you want to mark this PASCO as reviewed? This indicates that you have verified the PASCO information matches the metadata and content."
                                : "Are you sure you want to schedule this PASCO for review? This will mark it as needing review again."}
                        </p>
                    </ModalBody>
                    <ModalFooter>
                        <Button
                            variant="flat"
                            onPress={() => setConfirmModalOpen(false)}
                            className="rounded"
                        >
                            Cancel
                        </Button>
                        <Button
                            color={actionType === "review" ? "success" : "warning"}
                            onPress={confirmAction}
                            isLoading={isReviewing || isScheduling}
                            className="rounded"
                        >
                            {actionType === "review" ? "Mark as Reviewed" : "Schedule for Review"}
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </div>
    )
}